import { useState, useMemo, useCallback, useRef, useEffect, lazy, Suspense } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, AlertCircle, Loader2, Keyboard, FileSpreadsheet, FileText, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { exportSession } from '@/api/endpoints';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorBoundary, KeyboardShortcutsHelp, useToast } from '@/components/common';
import { useSessionStore } from '@/stores/useSessionStore';
import { useReviewStore } from '@/stores/useReviewStore';
import {
  VirtualizedQuestionList,
  type VirtualizedQuestionListHandle,
  ReviewFilterBar,
  BulkActionBar,
  ReviewSummaryBar,
  CoverageSummary,
  ReviewCockpit,
  useFilteredQuestions,
  useReviewActions,
  useBulkUpdateAction,
  isCockpitEnabled,
} from '@/features/review';
import {
  useSessionLoader,
  useSessionStatus,
  useReviewKeyboardShortcuts,
  REVIEW_SHORTCUTS,
} from '@/hooks';
import { isDocWorkspaceEnabled } from '@/features/workspace';

// Lazy-load the document workspace so the live editor (ProseMirror + jszip +
// docx editor) lands in a SEPARATE chunk that is only fetched when the
// flag-on workspace actually renders. With VITE_DOC_WORKSPACE off (default)
// this code is never imported, keeping it out of the main bundle. The feature
// uses named exports, so we map the default export we added on
// WorkspaceContainer. Importing the deep path (not the barrel) avoids pulling
// the whole workspace barrel — and the editor — eagerly.
const WorkspaceContainer = lazy(() =>
  import('@/features/workspace/components/WorkspaceContainer').then((m) => ({
    default: m.WorkspaceContainer,
  })),
);

/**
 * Review page wrapped with error boundary for graceful error handling.
 */
export function ReviewPage() {
  const handleError = useCallback((error: Error) => {
    console.error('[ReviewPage] Error caught by boundary:', error);
  }, []);

  return (
    <ErrorBoundary onError={handleError}>
      <ReviewPageContent />
    </ErrorBoundary>
  );
}

/**
 * Internal review page content component.
 */
function ReviewPageContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const storeQuestions = useSessionStore((s) => s.questions);
  const currentSession = useSessionStore((s) => s.currentSession);

  // Keyboard shortcuts state
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [currentNavigationIndex, setCurrentNavigationIndex] = useState(0);
  const questionListRef = useRef<VirtualizedQuestionListHandle>(null);

  // Load session from API
  const { isLoading: isLoadingSession, isError, error, refetch } = useSessionLoader(id ?? null);

  // Poll for status if session is still processing
  const { progress, isPolling } = useSessionStatus(id ?? null, {
    enabled: currentSession?.status === 'Processing' || currentSession?.status === 'Pending',
    onReady: () => {
      refetch();
    },
  });
  const {
    expandedQuestionId,
    expandQuestion,
    selectedQuestionIds,
    selectQuestion,
    deselectQuestion,
    selectAll,
    clearSelection,
    editingQuestionId,
    editingResponse,
    startEditing,
    updateEditingResponse,
    cancelEditing,
  } = useReviewStore();

  const { filteredQuestions, stats, filteredStats } = useFilteredQuestions();
  const { acceptQuestion, modifyQuestion, rejectQuestion } = useReviewActions();

  const {
    bulkAccept,
    bulkReject,
    bulkAcceptAboveThreshold,
    isPending: isBulkProcessing,
  } = useBulkUpdateAction(id ?? '');

  const questionIds = useMemo(
    () => filteredQuestions.map((q) => q.question_id),
    [filteredQuestions]
  );

  // The two-pane cockpit is the delivered review design; the legacy
  // expand/collapse list stays available behind the flag. When the cockpit is
  // active it owns the keyboard model (via ReviewCockpit), so the legacy
  // shortcut hook below is fed an empty question set to keep it inert and avoid
  // double-firing on the same window keydown.
  const cockpit = isCockpitEnabled();

  // Engine-agnostic document workspace (additive, flag-gated). When ON it
  // replaces the review content area with the live-document editor; the
  // WorkspaceContainer self-loads from the session id. When OFF (default) this
  // is byte-for-byte the existing cockpit/list behavior.
  const isWorkspace = isDocWorkspaceEnabled();

  // Scroll the expanded card to the top of the inner question list. Without
  // this, expanding a card near the viewport bottom hides its body below the
  // fold, and toggling expansion can leave the inner scroller positioned past
  // the question text — the bug looked like the sticky header was eating Q1.
  useEffect(() => {
    if (!expandedQuestionId) return;
    const idx = questionIds.indexOf(expandedQuestionId);
    if (idx >= 0) {
      questionListRef.current?.scrollToIndex(idx);
    }
  }, [expandedQuestionId, questionIds]);

  // Keyboard shortcut handlers
  const handleKeyboardNavigate = useCallback(
    (questionId: string) => {
      const index = questionIds.indexOf(questionId);
      if (index >= 0) {
        setCurrentNavigationIndex(index);
        expandQuestion(questionId);
      }
    },
    [questionIds, expandQuestion]
  );

  const handleKeyboardAccept = useCallback(
    (questionId: string) => {
      acceptQuestion(questionId);
    },
    [acceptQuestion]
  );

  const handleKeyboardModify = useCallback(
    (questionId: string) => {
      const question = filteredQuestions.find((q) => q.question_id === questionId);
      if (question) {
        startEditing(questionId, question.suggested_response);
      }
    },
    [filteredQuestions, startEditing]
  );

  const handleKeyboardReject = useCallback(
    (questionId: string) => {
      rejectQuestion(questionId);
    },
    [rejectQuestion]
  );

  const handleKeyboardToggleExpand = useCallback(
    (questionId: string) => {
      if (expandedQuestionId === questionId) {
        expandQuestion(null);
      } else {
        expandQuestion(questionId);
        const index = questionIds.indexOf(questionId);
        if (index >= 0) {
          setCurrentNavigationIndex(index);
        }
      }
    },
    [expandedQuestionId, expandQuestion, questionIds]
  );

  const handleKeyboardSaveEdit = useCallback(() => {
    if (editingQuestionId) {
      modifyQuestion(editingQuestionId, editingResponse);
    }
  }, [editingQuestionId, editingResponse, modifyQuestion]);

  const handleKeyboardCancelEdit = useCallback(() => {
    cancelEditing();
  }, [cancelEditing]);

  const handleToggleHelp = useCallback(() => {
    setIsHelpOpen((prev) => !prev);
  }, []);

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(
    async (format: 'xlsx' | 'csv', filter: 'all' | 'accepted') => {
      if (!id) return;

      setIsExporting(true);
      try {
        const blob = await exportSession(id, format, filter);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const timestamp = new Date().toISOString().split('T')[0];
        const filterSuffix = filter === 'accepted' ? '_accepted' : '';
        link.download = `ddq_session_${id}${filterSuffix}_${timestamp}.${format}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showSuccess('Export Complete', `Downloaded ${format.toUpperCase()} file`);
      } catch (err) {
        showError('Export Failed', err instanceof Error ? err.message : 'Failed to export session');
      } finally {
        setIsExporting(false);
      }
    },
    [id, showSuccess, showError]
  );

  // Initialize keyboard shortcuts
  useReviewKeyboardShortcuts(
    {
      currentIndex: currentNavigationIndex,
      totalQuestions: cockpit ? 0 : filteredQuestions.length,
      isEditing: editingQuestionId !== null,
      isHelpOpen,
      expandedQuestionId: cockpit ? null : expandedQuestionId,
      questionIds: cockpit ? [] : questionIds,
    },
    {
      onNavigate: handleKeyboardNavigate,
      onAccept: handleKeyboardAccept,
      onModify: handleKeyboardModify,
      onReject: handleKeyboardReject,
      onToggleExpand: handleKeyboardToggleExpand,
      onSaveEdit: handleKeyboardSaveEdit,
      onCancelEdit: handleKeyboardCancelEdit,
      onToggleHelp: handleToggleHelp,
    }
  );

  const handleToggleSelect = (questionId: string) => {
    if (selectedQuestionIds.has(questionId)) {
      deselectQuestion(questionId);
    } else {
      selectQuestion(questionId);
    }
  };

  const handleSelectAll = () => {
    selectAll(filteredQuestions.map((q) => q.question_id));
  };

  const handleAcceptAboveThreshold = async (threshold: number) => {
    try {
      const count = await bulkAcceptAboveThreshold(threshold, filteredQuestions);
      showSuccess('Bulk Accept Complete', `Accepted ${count} questions with confidence >= ${threshold}%`);
    } catch (err) {
      showError('Bulk Accept Failed', err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleAcceptSelected = async () => {
    try {
      const selectedIds = Array.from(selectedQuestionIds);
      const count = await bulkAccept(selectedIds);
      showSuccess('Bulk Accept Complete', `Accepted ${count} selected questions`);
    } catch (err) {
      showError('Bulk Accept Failed', err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleRejectSelected = async () => {
    try {
      const selectedIds = Array.from(selectedQuestionIds);
      const count = await bulkReject(selectedIds);
      showSuccess('Bulk Reject Complete', `Rejected ${count} selected questions`);
    } catch (err) {
      showError('Bulk Reject Failed', err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleSaveEdit = (questionId: string) => {
    modifyQuestion(questionId, editingResponse);
  };

  // Cockpit batch lane: accept all safe vault-grounded answers in one action.
  const handleBatchAccept = async (ids: string[]) => {
    try {
      const count = await bulkAccept(ids);
      showSuccess('Auto-accept Complete', `Accepted ${count} vault-grounded answers`);
    } catch (err) {
      showError('Auto-accept Failed', err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const isLoading = isLoadingSession || storeQuestions.length === 0;
  const isProcessing = isPolling || currentSession?.status === 'Processing';

  // Error state
  if (isError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/sessions">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">Review Questionnaire</h1>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">We couldn't load this questionnaire</h2>
            <p className="text-muted-foreground mb-4">
              {error instanceof Error ? error.message : 'It may still be processing. Try again in a moment.'}
            </p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => refetch()}>Try Again</Button>
              <Button variant="outline" onClick={() => navigate('/sessions')}>
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Processing state
  if (isProcessing && progress) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/sessions">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Processing Questionnaire</h1>
            <p className="text-sm text-muted-foreground">
              {currentSession?.fileName || `Session #${id}`}
            </p>
          </div>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
            <h2 className="text-lg font-semibold mb-2">Processing questions...</h2>
            <p className="text-muted-foreground mb-4">
              {progress.processed_questions} of {progress.total_questions} questions processed
            </p>
            <Progress value={progress.progress_percent} className="max-w-md mx-auto h-2" />
            <p className="text-sm text-muted-foreground mt-2">
              {progress.progress_percent.toFixed(1)}% complete
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasSelection = selectedQuestionIds.size > 0;
  const reviewedCount = stats.accepted + stats.modified + stats.rejected;

  return (
    <div className={`space-y-6 ${hasSelection ? 'pb-20' : ''}`}>
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/sessions">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Review Questionnaire</h1>
            <p className="text-sm text-muted-foreground">
              {currentSession?.fileName || `Session #${id}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggleHelp}
            title="Keyboard shortcuts (?)"
          >
            <Keyboard className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={isExporting || isLoading}>
                {isExporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Export Format</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExport('xlsx', 'all')}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Excel (.xlsx) - All
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('xlsx', 'accepted')}>
                <Check className="h-4 w-4 mr-2" />
                Excel (.xlsx) - Accepted Only
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExport('csv', 'all')}>
                <FileText className="h-4 w-4 mr-2" />
                CSV - All
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv', 'accepted')}>
                <Check className="h-4 w-4 mr-2" />
                CSV - Accepted Only
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Summary Bar - replaces 5 stat cards */}
      {isLoading ? (
        <Card>
          <CardContent className="py-4">
            <Skeleton className="h-5 w-3/4 mb-3" />
            <Skeleton className="h-2.5 w-full rounded-full" />
          </CardContent>
        </Card>
      ) : (
        <ReviewSummaryBar
          totalQuestions={stats.totalQuestions}
          exactMatches={stats.exactMatches}
          suggestedMatches={stats.suggestedMatches}
          generatedResponses={stats.generatedResponses}
          noMatches={stats.noMatches}
          reviewed={reviewedCount}
        />
      )}

      {/* Coverage Report (F3): every question answered or explained */}
      {!isLoading && <CoverageSummary sessionId={id ?? null} />}

      {/* Filter Bar */}
      <ReviewFilterBar />

      {/* Bulk Actions — legacy list only; the cockpit has its own batch lane */}
      {!cockpit && (
        <BulkActionBar
          selectedCount={selectedQuestionIds.size}
          totalCount={filteredQuestions.length}
          pendingCount={filteredStats.pending}
          onAcceptSelected={handleAcceptSelected}
          onRejectSelected={handleRejectSelected}
          onAcceptAboveThreshold={handleAcceptAboveThreshold}
          onSelectAll={handleSelectAll}
          onClearSelection={clearSelection}
          isProcessing={isBulkProcessing}
        />
      )}

      {/* Questions: two-pane cockpit (default) or legacy expand/collapse list */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="border-l-4 border-l-muted">
              <CardContent className="pt-4">
                <div className="space-y-3">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : isWorkspace && id ? (
        // Bound the workspace to the viewport (minus the app + page chrome) so the
        // editor and Q&A panes each scroll INTERNALLY instead of scrolling the page.
        <div className="h-[calc(100vh-11rem)] overflow-hidden">
          <Suspense
            fallback={
              <Card className="h-full">
                <CardContent className="flex h-full items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">
                    Loading document workspace…
                  </span>
                </CardContent>
              </Card>
            }
          >
            <WorkspaceContainer sessionId={id} />
          </Suspense>
        </div>
      ) : cockpit ? (
        <ReviewCockpit
          questions={filteredQuestions}
          sessionId={id ?? null}
          editingQuestionId={editingQuestionId}
          editingResponse={editingResponse}
          keyboardEnabled={!isHelpOpen}
          onAccept={acceptQuestion}
          onReject={rejectQuestion}
          onStartEdit={startEditing}
          onSaveEdit={handleSaveEdit}
          onCancelEdit={cancelEditing}
          onUpdateEditingResponse={updateEditingResponse}
          onBatchAccept={handleBatchAccept}
          isBatchProcessing={isBulkProcessing}
        />
      ) : (
        <VirtualizedQuestionList
          ref={questionListRef}
          questions={filteredQuestions}
          expandedQuestionId={expandedQuestionId}
          selectedQuestionIds={selectedQuestionIds}
          editingQuestionId={editingQuestionId}
          editingResponse={editingResponse}
          onToggleExpand={expandQuestion}
          onToggleSelect={handleToggleSelect}
          onAccept={acceptQuestion}
          onReject={rejectQuestion}
          onStartEdit={startEditing}
          onSaveEdit={handleSaveEdit}
          onCancelEdit={cancelEditing}
          onUpdateEditingResponse={updateEditingResponse}
        />
      )}

      {/* Review Progress Summary */}
      {!isLoading && stats.completionRate > 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium">Review Progress</p>
                <p className="text-xs text-muted-foreground">
                  {reviewedCount} of {stats.totalQuestions}{' '}
                  questions reviewed
                </p>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-green-600">
                  <span className="font-medium">{stats.accepted}</span> accepted
                </span>
                <span className="text-blue-600">
                  <span className="font-medium">{stats.modified}</span> modified
                </span>
                <span className="text-red-600">
                  <span className="font-medium">{stats.rejected}</span> skipped
                </span>
              </div>
            </div>
            <Progress value={stats.completionRate} className="mt-3 h-2" />
          </CardContent>
        </Card>
      )}

      {/* Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsHelp
        isOpen={isHelpOpen}
        onClose={handleToggleHelp}
        shortcuts={REVIEW_SHORTCUTS}
        title="Review Keyboard Shortcuts"
      />
    </div>
  );
}
